<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-toolbar flat color="toolbar">
      <v-col md="4" lg="4" xl="3" class="pl-0 pr-2">
        <v-autocomplete v-if="!isTprProRetailUser"
          v-model="selectedAdGroup"
          :items="userAdGroups"
          item-text="name"
          item-value="id"
          outlined
          return-object
          dense
          class="mt-7 ma-0"
          background-color="input"
          label="Ad Group"
          @change="onSelectionChange"
        ></v-autocomplete>
        <div v-else class="d-flex align-start">
          <v-autocomplete
            v-model="store"
            :items="userStores"
            background-color="input"
            class="mt-7 ma-0 flex-grow-1"
            dense
            item-text="display_name"
            item-id="store_party_id"
            label="Store Group / Store"
            :menu-props="{ maxHeight: 210 }"
            outlined
            return-object
            @change="onSelectionChange">
          </v-autocomplete>
          <v-btn
            v-if="storeGroupIsSelected"
            height="40"
            depressed
            color="primary"
            class="mt-7 flex-grow-0"
            style="border-top-left-radius: 0px; border-bottom-left-radius: 0px; border: 1px solid #9e9e9e; border-left: 0px;"
            @click="storeGroupModal = true">
            <v-icon color="white">mdi-store</v-icon>
          </v-btn>
        </div>
      </v-col>
      <v-tooltip v-if="storeGroupIsSelected" bottom>
        <template #activator="{ on, attrs }">
          <v-icon color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
        </template>
        <span>Only rules shared by every store in the group are shown.</span>
      </v-tooltip>
      <v-spacer></v-spacer>
      <v-col class="pr-6 d-flex justify-end align-center">
        <v-tooltip v-if="storeGroupIsSelected && ruleDrift" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              outlined
              color="warning"
              class="ml-2 mr-3"
              height="38"
              min-width="38"
              style="padding: 0 10px;"
              v-bind="attrs"
              v-on="on"
              @click="deviationsModal = true">
              <v-icon>mdi-alert</v-icon>
            </v-btn>
          </template>
          <span>Some stores in this group have differing rules.</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :disabled="disableRuleCreation"
              small
              class="blue white--text  ml-2"
              height="38"
              v-bind="attrs"
              v-on="on"
              @click="openRulesModal">
              create
            </v-btn>
          </template>
          <span>Create Rules</span>
        </v-tooltip>
      </v-col>
    </v-toolbar>

    <div v-if="isLoading" class="d-flex justify-center align-center" style="height: 300px;">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-data-table 
      v-if="!isLoading"
      :headers="computedHeaders"
      :items="orderedRules"
      :items-per-page="-1"
      :no-data-text="noDataText"
      dense
      class="elevation-1"
      hide-default-footer
      group-by="priority">

      <template v-slot:[`group.header`]="{group, headers, toggle, isOpen, items}">
        <td :colspan="headers.length" class="ruleNames font-weight-bold">
          {{ group + ". " + items[0].rule_display_name }}
        </td>
      </template>

      <template v-slot:[`item.perishable`]="{ item }">
        <span>{{ formatBatchType(item.perishable) }}</span>
      </template>

      <template v-slot:[`item.perishable`]="{ item }">
        <v-chip v-for="(type) in getBatchTypes(item.perishable)"
          class="mr-2"
          :key="type"
          small
          :color="type == 'Perishable' ? 'amber darken-3' : 'primary'"
          text-color="white">
          <span 
            style="
              width: 100px;
              text-align: center;">
            {{type}}
          </span>
        </v-chip>
      </template>

      <template v-slot:[`item.rule_value`]="{ item }">
        <span v-if="item.label === 'Bracket'">
          {{ formatBrackets(item.brackets) }}
        </span>
        <span v-else-if="item.label === 'CompetitiveIndex' || item.label === 'CompetitiveMatch'" class="d-flex align-center">
          {{ item.label === 'CompetitiveMatch' ? 'Match' : formatRuleValue(item.rule_value, 'Percentage') }}
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <span>{{ competitiveSummary(item) }}</span>
          </v-tooltip>
        </span>
        <span
          v-else-if="item.label != 'None'">
          {{ formatRuleValue(item.rule_value, item.label) }}
        </span>
        <v-icon v-else color="green">mdi-arrow-up-circle</v-icon>
      </template>

      <template v-slot:[`item.enabled`]="{ item }">
        <v-chip small :color="item.enabled === false ? 'grey' : 'green'" text-color="white">
          {{ item.enabled === false ? 'Disabled' : 'Enabled' }}
        </v-chip>
      </template>

      <template v-slot:[`item.filter_lvl`]="{ item }">
        <span v-if="item.filter_lvl">{{ getRuleDisplay(item.filter_lvl) }}</span>
        <v-icon v-else color="red">mdi-close-circle</v-icon>
      </template>

      <template v-slot:[`item.filter_value`]="{ item }">
        <span v-if="item.filter_value">{{ item.filter_value }}</span>
        <v-icon v-else color="red">mdi-close-circle</v-icon>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu offset-y>
          <template #activator="{ on }">
            <v-btn icon v-on="on">
              <v-icon>mdi-pencil-box-outline</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-if="isTprProRetailUser && !storeGroupIsSelected" @click="openShuttleModal(item)">
              <v-list-item-icon>
                <v-icon>mdi-truck-delivery-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Shuttle</v-list-item-title>
            </v-list-item>
            <v-list-item @click="editRule(item)">
              <v-list-item-icon>
                <v-icon>mdi-pencil</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Edit</v-list-item-title>
            </v-list-item>
            <v-list-item @click="toggleEnabled(item)">
              <v-list-item-icon>
                <v-icon>{{ item.enabled === false ? 'mdi-check-circle-outline' : 'mdi-cancel' }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ item.enabled === false ? 'Enable' : 'Disable' }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleteRule(item)">
              <v-list-item-icon>
                <v-icon color="red">mdi-trash-can</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <v-dialog
      v-model="isRulesModalOpen"
      :max-width="isDefinitionsVisible ? '1200px' : '700px'"
      :persistent="isSavingRule"
      @click:outside="closeRulesModal">
      <v-form @submit.prevent v-model="valid">
        <v-card>
          <v-card-title class="main white--text pa-2 d-flex justify-space-between">
            <span> Rule Manager </span>
            <v-icon
              class="ml-auto"
              color="grey lighten-1"
              @click="toggleDefinitions"
            >mdi-information-outline</v-icon>
          </v-card-title>

          <v-card-text class="pa-3">
            <v-row>
              <v-col :cols="isDefinitionsVisible ? 6 : 12" class="pr-3" style="border-right: 1px solid #e0e0e0;">
                <v-stepper v-model="step" vertical linear class="mb-5">
                  
                  <!-- Step 1: Rule Selection -->
                  <v-stepper-step :complete="isRuleSelected" :editable="isRuleSelected || !!editingRuleId" step="1">
                    Select Rule <span v-if="selectedRule.rule_display_name" class="displayedValues"> {{ selectedRule.rule_display_name }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="1">
                    <p class="text-caption mt-0 mb-2">Choose how this rule sets or adjusts price.</p>
                    <v-row dense>
                      <v-col cols="8">
                        <v-select
                          class="pt-1"
                          v-model="selectedRule.rule_display_name"
                          :items="ruleOptions"
                          label="Select Rule"
                          item-text="display_name"
                          item-value="display_name"
                          outlined
                          :rules="ruleSelect"
                          dense
                          @change="setInitialValue"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-stepper-content>

                  <!-- Step 2: Rule Value Input -->
                  <v-stepper-step :complete="isValueEntered" :editable="isRuleSelected || !!editingRuleId" step="2">
                    Enter Value
                    <span v-if="competitiveValuePreview" class="displayedValues"> {{ competitiveValuePreview }}</span>
                    <span v-else-if="selectedRule.rule_value" class="displayedValues"> {{ selectedRule.rule_value }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="2">
                    <v-row dense v-if="selectedRule.label === 'Number' || selectedRule.label === 'Percentage'">
                      <v-col cols="6">
                        <v-text-field
                          class="pt-1"
                          v-model="selectedRule.rule_value"
                          label="Value"
                          outlined
                          placeholder="Enter value"
                          :rules="getRuleValueRules()"
                          dense
                          type="number"
                          :step="selectedRule.rule_name === 'minimum_discount' ? 0.1 : 1"
                          :min="0"
                          @change="handleValueInput"
                          @keydown.enter="handleValueInput"
                        >
                          <template v-slot:prepend-inner>
                            <span v-if="selectedRule.label === 'Number'">$</span>
                            <span v-if="selectedRule.label === 'Percentage'">%</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <!-- Price bracketing editor: map a calculated per-unit price range to a multi-unit price -->
                    <div v-else-if="selectedRule.label === 'Bracket'">
                      <p class="text-caption mb-1">
                        When a calculated per-unit price falls in a range, convert it to a multi-unit price (multiplier / $price).
                      </p>
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th class="text-left">Min ($/unit)</th>
                            <th class="text-left">Max ($/unit)</th>
                            <th class="text-left" style="width: 90px;">Multiplier/Units</th>
                            <th class="text-left">Price ($)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(bracket, index) in selectedRule.brackets" :key="index">
                            <td><v-text-field v-model.number="bracket.min" type="number" min="0" step="0.01" dense hide-details outlined class="pt-1" prefix="$"></v-text-field></td>
                            <td><v-text-field v-model.number="bracket.max" type="number" min="0" step="0.01" dense hide-details outlined class="pt-1" prefix="$"></v-text-field></td>
                            <td style="width: 90px;"><v-text-field v-model.number="bracket.units" type="number" min="1" step="1" dense hide-details outlined class="pt-1" style="max-width: 90px;"></v-text-field></td>
                            <td><v-text-field v-model.number="bracket.price" type="number" min="0" step="0.01" dense hide-details outlined class="pt-1" prefix="$"></v-text-field></td>
                            <td>
                              <v-btn icon small @click="removeBracket(index)" :disabled="selectedRule.brackets.length <= 1">
                                <v-icon small color="red">mdi-close</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                      <v-btn text small color="primary" class="mt-1" @click="addBracket">
                        <v-icon small left>mdi-plus</v-icon> Add Bracket
                      </v-btn>
                      <div>
                        <v-btn small color="primary" class="mt-2" @click="handleBracketInput">Next</v-btn>
                      </div>
                    </div>

                    <!-- Competitive Index Pricing - %: competitor + index % + optional margin floor + rounding -->
                    <v-row dense v-else-if="selectedRule.label === 'CompetitiveIndex'">
                      <v-col cols="12">
                        <v-alert v-if="competitorDivergence" type="warning" dense text class="mb-2 py-1 text-caption">
                          Stores in this group have different competitor lists; the selected competitor may not exist for all stores.
                        </v-alert>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <v-select
                            class="pt-1 flex-grow-1"
                            v-model="selectedRule.competitor"
                            :items="competitorItems"
                            item-text="label"
                            item-value="key"
                            label="Competitor"
                            outlined
                            dense
                            :rules="competitorRules"
                            :disabled="competitorItems.length === 0"
                            :loading="isLoadingCompetitors"
                            :hint="!isLoadingCompetitors && competitorItems.length === 0 ? 'No competitors available for this store\'s DMA' : ''"
                            persistent-hint
                            @change="handleCompetitiveInput"
                          ></v-select>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" class="ml-1 mt-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>The competitor banner this rule prices against — your SRP is calculated from this banner's retail price. Only banners available in your store's DMA are listed.</span>
                          </v-tooltip>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <v-text-field
                            class="pt-1 flex-grow-1"
                            v-model="selectedRule.rule_value"
                            label="Index %"
                            type="number"
                            step="0.01"
                            outlined
                            dense
                            :rules="indexPctRules()"
                            @change="handleCompetitiveInput"
                            @keydown.enter="handleCompetitiveInput"
                          >
                            <template v-slot:prepend-inner><span>%</span></template>
                          </v-text-field>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" class="ml-1 mt-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>Percentage applied to the competitor's retail to set your SRP. Positive prices above the competitor (e.g. 10 = 10% higher); negative prices below. The result is rounded to the nearest cent.</span>
                          </v-tooltip>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <v-text-field
                            class="pt-1 flex-grow-1"
                            v-model="selectedRule.margin_threshold"
                            label="Min. margin (optional)"
                            type="number"
                            step="0.01"
                            outlined
                            dense
                            :rules="marginThresholdRules()"
                          >
                            <template v-slot:prepend-inner><span>%</span></template>
                          </v-text-field>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" class="ml-1 mt-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>The lowest gross margin % (freight-inclusive) you'll accept on the resulting SRP. If the competitive price would land below this floor, this rule is skipped and the next-priority rule prices the item instead. Leave blank for no floor; negative values are allowed.</span>
                          </v-tooltip>
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <div class="d-flex align-center">
                          <span class="text-caption mr-1">Rounding</span>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" small class="mr-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>Controls rounding of the competitive price. "Apply my rounding rule after" runs your store's configured rounding rule on the calculated SRP; "No rounding" keeps the exact calculated price.</span>
                          </v-tooltip>
                          <v-radio-group v-model="selectedRule.apply_rounding" row dense hide-details class="mt-0 pt-0">
                            <v-radio label="No rounding" :value="false"></v-radio>
                            <v-radio label="Apply my rounding rule after" :value="true"></v-radio>
                          </v-radio-group>
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <v-btn small color="primary" @click="advanceCompetitiveStep">Next</v-btn>
                      </v-col>
                    </v-row>

                    <!-- Competitive Match: competitor + optional margin floor + rounding (no index %) -->
                    <v-row dense v-else-if="selectedRule.label === 'CompetitiveMatch'">
                      <v-col cols="12">
                        <v-alert v-if="competitorDivergence" type="warning" dense text class="mb-2 py-1 text-caption">
                          Stores in this group have different competitor lists; the selected competitor may not exist for all stores.
                        </v-alert>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <v-select
                            class="pt-1 flex-grow-1"
                            v-model="selectedRule.competitor"
                            :items="competitorItems"
                            item-text="label"
                            item-value="key"
                            label="Competitor"
                            outlined
                            dense
                            :rules="competitorRules"
                            :disabled="competitorItems.length === 0"
                            :loading="isLoadingCompetitors"
                            :hint="!isLoadingCompetitors && competitorItems.length === 0 ? 'No competitors available for this store\'s DMA' : ''"
                            persistent-hint
                            @change="handleCompetitiveInput"
                          ></v-select>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" class="ml-1 mt-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>The competitor banner this rule matches — your SRP is set to this banner's retail price exactly. Only banners available in your store's DMA are listed.</span>
                          </v-tooltip>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="d-flex align-center">
                          <v-text-field
                            class="pt-1 flex-grow-1"
                            v-model="selectedRule.margin_threshold"
                            label="Min. margin (optional)"
                            type="number"
                            step="0.01"
                            outlined
                            dense
                            :rules="marginThresholdRules()"
                          >
                            <template v-slot:prepend-inner><span>%</span></template>
                          </v-text-field>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" class="ml-1 mt-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>The lowest gross margin % (freight-inclusive) you'll accept on the resulting SRP. If the competitive price would land below this floor, this rule is skipped and the next-priority rule prices the item instead. Leave blank for no floor; negative values are allowed.</span>
                          </v-tooltip>
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <div class="d-flex align-center">
                          <span class="text-caption mr-1">Rounding</span>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon color="grey" small class="mr-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>Controls rounding of the competitive price. "Apply my rounding rule after" runs your store's configured rounding rule on the calculated SRP; "No rounding" keeps the exact calculated price.</span>
                          </v-tooltip>
                          <v-radio-group v-model="selectedRule.apply_rounding" row dense hide-details class="mt-0 pt-0">
                            <v-radio label="No rounding" :value="false"></v-radio>
                            <v-radio label="Apply my rounding rule after" :value="true"></v-radio>
                          </v-radio-group>
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <v-btn small color="primary" @click="advanceCompetitiveStep">Next</v-btn>
                      </v-col>
                    </v-row>
                  </v-stepper-content>

                  <!-- Step 3: Batch Type Selection -->
                  <v-stepper-step :complete="isBatchTypeComplete" :editable="isValueEntered || !!editingRuleId" step="3">
                    Batch Type (Optional)
                  <span v-if="selectedRule.perishable != null" class="displayedValues"> {{ selectedRule.perishable == true ? 'Perishable' : 'Non Perishable' }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="3">
                    <p class="text-caption mt-0 mb-2">Optional — limit this rule to perishable or non-perishable batches.</p>
                    <v-row>
                      <v-col cols="8">
                        <v-select
                          class="pt-1"
                          v-model="selectedRule.perishable"
                          :items="batchTypes"
                          item-text="name"
                          item-value="value"
                          label="Select Batch Type"
                          outlined
                          clearable
                          dense
                          @change="handleBatchTypeInput"
                        >
                        </v-select>
                      </v-col>
                    </v-row>
                    <v-btn @click="skipBatchTypeStep" color="primary" small class="mt-3">{{ selectedRule.perishable != null ? 'Continue' : 'Skip' }}</v-btn>
                  </v-stepper-content>

                  <!-- Step 4: Filter Selection -->
                  <v-stepper-step :complete="isFilterComplete" :editable="isValueEntered || !!editingRuleId" step="4">
                    Filters (Optional)
                  <span v-if="displayFilterText" class="displayedValues"> {{ displayFilterText }}</span>
                  </v-stepper-step>
                  <v-stepper-content step="4">
                    <p class="text-caption mt-0 mb-2">Optional — narrow this rule to departments, categories, or subcategories. If you choose more than one level, only the most specific one applies.</p>
                    <v-row v-for="level in filterLevels" :key="level.key">
                      <v-col cols="8">
                        <HierarchyFilterSelect
                          :value="level.selection"
                          :items="level.items"
                          :label="level.label"
                          :loading="isLoadingFilters"
                          @input="onLevelInput(level.key, $event)"
                        />
                      </v-col>
                    </v-row>
                    <v-alert v-if="filterOverrideNotice" type="info" dense text class="mt-2 mb-1 py-1 text-caption">{{ filterOverrideNotice }}</v-alert>
                    <p class="text-caption mt-2 mb-0 font-weight-medium primary--text">{{ rulesetPreview }}</p>
                    <v-btn @click="skipFilterStep" color="primary" small class="mt-3">{{ displayFilterText ? 'Continue' : 'Skip' }}</v-btn>
                  </v-stepper-content>

                  <!-- step 5 -->
                  <v-stepper-step :complete="isRulesComplete" :editable="isFilterComplete" step="5">
                    Complete
                  </v-stepper-step>
                  <v-stepper-content step="5">
                    <v-row dense>
                      <v-col cols="2" class="pt-1">
                        <v-btn @click="completeRuleset" color="primary" class="mb-4" small :loading="isSavingRule" :disabled="isSavingRule">Complete</v-btn>
                      </v-col>
                    </v-row>
                  </v-stepper-content>
                </v-stepper> 
              </v-col>

              <v-col v-if="isDefinitionsVisible" cols="6" class="pl-3">
                <h3 class="definitions-title" style="border-bottom: 1px solid #e0e0e0;">Definitions</h3>
                  <div
                    v-for="(definition, index) in definitions"
                    :key="index"
                    class="definition-term"
                  >
                  {{ index + 1 }}. <strong>{{ definition.term }}</strong>
                  <ul class="definition-list">
                    <li v-for="condition in formatDescription(definition.description)" :key="condition">{{ condition }}</li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-3 pb-3 pt-0">
            <span v-if="displayFilterText" class="text-caption grey--text">{{ displayFilterText }}</span>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              small
              :loading="isSavingRule"
              :disabled="!canSaveRule || isSavingRule"
              @click="completeRuleset"
            >Save Rule</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <RulesetShuttle
      :value.sync="isShuttleModalOpen"
      :ruleset="shuttleRuleset"
      @shuttled="getRulesets"
    />
    <StoreGroupModal
      :value.sync="storeGroupModal"
      :storeParty="store">
    </StoreGroupModal>
    <RuleDeviationsModal
      :value.sync="deviationsModal"
      :storeName="storeGroupIsSelected ? store.store_name : ''"
      :deviations="ruleDeviations"
      :loading="isLoading"
      @apply-to-all="applyDeviationToAll"
      @remove-from-all="removeDeviationFromAll">
    </RuleDeviationsModal>
  </v-container>
</template>
<script>
import { fullwidth } from "@/mixins/fullwidth"
import { paging } from "@/mixins/paging"
import { displayHelpers } from "@/mixins/displayHelpers"
import { userAccess } from "@/mixins/user-access"
import { utils } from "@/mixins/utils"
import { shared } from "@/mixins/shared"
import Rules from "@/axios/rules-management"
import { buildCompetitiveRuleEntry, competitiveSignatureFields, indexPctRules, marginThresholdRules, competitorKeyToLabel, isCompetitiveRuleName } from "@/utils/competitiveRules"
import RulesetShuttle from "@/components/RulesetShuttle.vue"
import StoreGroupModal from "@/components/StoreGroupModal.vue"
import RuleDeviationsModal from "@/components/RuleDeviationsModal.vue"
import HierarchyFilterSelect from "@/components/HierarchyFilterSelect.vue"

export default {
  name: "TprPro",
  components: {
    RulesetShuttle,
    StoreGroupModal,
    RuleDeviationsModal,
    HierarchyFilterSelect
  },
  mixins: [
    fullwidth,
    displayHelpers,
    paging,
    userAccess,
    utils,
    shared
  ],
  data() {
    return {
      selectedAdGroup: {},
      store: {},
      userStores: [],
      step: 1,
      level: '',
      filterStepperOpen: false,
      departmentOptions: [],
      departmentCategoryPairs: [],
      categorySubcategoryPairs: [],
      selectedDepartments: [],
      selectedCategories: [],
      selectedSubcategories: [],
      batchTypes: [
        { name: "PERISHABLE", value: true },
        { name: "NON PERISHABLE", value: false }
      ],
      selectedRule: {},
      ruleOptions: [],
      competitorItems: [],
      isLoadingCompetitors: false,
      competitorDivergence: false,
      ruleSelect: [
        value => {
          if (value) return true
          return 'Rule selection is required'
        }
      ],
      rulesData: [],
      relationships: [],
      isRulesModalOpen: false,
      isRuleSelected: false,
      isValueEntered: false, 
      valid: false,
      editingRuleId: null,
      isFilterComplete: false,
      isBatchTypeComplete: false,
      isRulesComplete: false,
      definitions: [
        { term: "Competitive Index Pricing - %", description: "Sets your TPR SRP to the selected DMA competitor's retail price adjusted by an index percentage (e.g. +10% prices 10% above the competitor). Applies to all eligible items and supersedes pass-through % and gross-margin rules where it applies. If a minimum margin threshold is set and the resulting price falls below it, this rule is skipped and the next-priority rule prices the item. If rounding is enabled, your store's rounding rule is applied to the result." },
        { term: "Competitive Index Pricing - Top Sellers %", description: "Works like Competitive Index Pricing - % but applies only to items flagged as top sellers, so you can price top sellers against a competitor while other rules cover the remaining items. If the minimum margin threshold would be breached, this rule is skipped and the next-priority rule prices the item. If rounding is enabled, your store's rounding rule is applied to the result." },
        { term: "Competitive Index Pricing - Top Seller Match", description: "Sets your TPR SRP to match the selected DMA competitor's retail price exactly, with no index adjustment. Applies only to top-seller items. If the minimum margin threshold would be breached, this rule is skipped and the next-priority rule prices the item. If rounding is enabled, your store's rounding rule is applied to the result." },
        { term: "Pass Through Percent", description: "Sets the TPR SRP by passing a percentage of the item's deal allowances through to the shelf as a discount (e.g. 80% subtracts 80% of the per-unit allowance from regular retail). A higher percentage gives a deeper discount." },
        { term: "Gross Profit Percent", description: "Sets the TPR SRP to achieve a target gross profit percentage (SRP = net unit cost / (1 - target%)). If the item is not perishable, the price is capped so it never rises above the maximum allowable pass-through SRP." },
        { term: "Minimum Discount", description: "Enforces a minimum dollar discount off regular retail. If the item's discount (regular retail - promotional price) is less than this amount, the item is excluded from the TPR." },
        { term: "Price Bracketing", description: "Converts a calculated per-unit SRP into a multi-unit promotional price (e.g. 2/$3) based on retailer-defined price ranges. If the per-unit price falls within a bracket's min-max range, the SRP is set to that bracket's multiplier and price." },
        { term: "Rounding down if < 5 up if > 5", description: "If last digit is 0, subtract .01. If last digit > 5, move up to 9. If last digit < 5 move down to 9" },
        { term: "Rounding up to 5 or 9", description: "If last digit < 5 set to 5. If last digit 6 to 8 set to 9" },
        { term: "Rounding up to 7 or 9", description: "If last digit < 7 set to 7. If last digit is 8 set to 9" },
        { term: "Rounding up to 5 or 9 minus 1 on 0", description: "If last digit is 0, subtract .01. If 1 through 5, set to 5. If 6 through 8, set to 9" },
        { term: "Rounding up to 9", description: "If last digit is 0 through 8, set up to 9." },
        { term: "Rounding up to nearest odd number minus 1 on 0", description: "If last digit is 0, subtract .01. If 1 through 2, move up to 3. If 4, set to 5. If 6, set to 7. If 8, set to 9" },

      ],
      isDefinitionsVisible: false,
      isLoading: false,
      isSavingRule: false,
      isLoadingFilters: false,
      isShuttleModalOpen: false,
      shuttleRuleset: {},
      ruleDrift: false,
      editingGroupTargets: null,
      storeGroupModal: false,
      deviationsModal: false,
      ruleDeviations: []
    }
  },
  mounted() {
    this.initializeData()
  },
  watch: {
    userAdGroups: {
      handler(newValue) {
        if (newValue?.length > 0 && !sessionStorage.tpr_pro_ad_group) {
          let index = 0
          this.selectedAdGroup = newValue[index]
        }
      },
      deep: true
    },
    store(newValue) {
      if (newValue){
        this.saveState()
        // this.getRulesets()
      }
    },
    selectedAdGroup(newValue){
      if(newValue) {
        this.saveState()
        // this.getRulesets()
      }
    },
    async viewMode() {
      if (this.$auth.tenant !== 'alliance-retail-group') return
      if (this.isTprProRetailUser) {
        if (this.userStores.length === 0) await this.getStores()
        if (this.userStores.length === 0) {
          this.alert('You are not associated with any TPR Pro stores. Please contact support for access.')
          this.$router.replace('/')
          return
        }
        if (!this.store?.store_party_id) this.store = this.firstSelectableStore()
      } else if (!this.selectedAdGroup?.id && this.userAdGroups.length > 0) {
        this.selectedAdGroup = this.userAdGroups[0]
      }
      this.onSelectionChange()
    },
  },
  computed: {
    isTprProRetailUser() {
      if (this.adminUser) return this.viewMode === 'STORE'
      return this.userGroups.includes('tpr-manager.pro')
    },
    useStoreParties() {
      return this.isTprProRetailUser && this.$auth.tenant === 'alliance-retail-group'
    },
    storeGroupIsSelected() {
      return this.useStoreParties && this.store?.party_type === 'TPR_STORE_GROUP'
    },
    groupChildStores() {
      return this.storeGroupIsSelected ? (this.store.children || []) : []
    },
    disableRuleCreation() {
      if (this.isTprProRetailUser)
        return this.userStores.length === 0 || !this.store || !this.store.store_party_id ||
          (this.storeGroupIsSelected && this.groupChildStores.length === 0)
      else
        return this.userAdGroups.length == 0
    },
    competitiveValuePreview() {
      // step-2 header preview; '' for non-competitive rules (header falls back to rule_value).
      return this.competitiveSummary(this.selectedRule)
    },
    orderedRules() {
      if (this.rulesData.length == 0)
        return []

      let priority_rankings = this.rulesData.reduce((acc, rule) => {
        const groupName = rule.rule_display_name
        if (!acc.find(item => item.name === groupName)) {
          acc.push({"name": groupName, "priority": rule.rule_priority})
        }
        return acc
      }, [])
      priority_rankings = priority_rankings.sort((a, b) => a.priority - b.priority)

      return this.rulesData.map(item => ({
        ...item,
        priority: priority_rankings.findIndex(rule => rule.name === item.rule_display_name) + 1
      }))
    },
    computedHeaders() {
      return [
        { text: "Rule", value: "rule_display_name", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Batch Type", value: "perishable", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Rule Value", value: "rule_value", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Rule Filter", value: "filter_lvl", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Rule Filter Value", value: "filter_value", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Status", value: "enabled", sortable: false, align: "start", class: 'accent white--text' },
        { text: "Actions", value: "actions", sortable: false, align: "start", class: 'accent white--text' }
      ]
    },
    displayFilterText() {
      const { filterLvl, targets } = this.resolveFilterTargets()
      return targets.length ? `${this.getRuleDisplay(filterLvl)} - ${targets.join(', ')}` : ''
    },
    filterLevels() {
      return [
        { key: 'department', label: 'Select Department(s)', items: this.filteredDepartments, selection: this.selectedDepartments },
        { key: 'category', label: 'Select Category(ies)', items: this.filteredCategories, selection: this.selectedCategories },
        { key: 'subcategory', label: 'Select Subcategory(ies)', items: this.filteredSubcategories, selection: this.selectedSubcategories }
      ]
    },
    // Required-competitor validator (blocks empty/empty-DMA selection).
    competitorRules() {
      return [value => !!value || 'Competitor is required']
    },
    // Gates the persistent Save button: valid rule + value entered.
    canSaveRule() {
      if (!this.valid || !this.isRuleSelected) return false
      if (this.selectedRule.label === 'Bracket') {
        const cleaned = this.validBrackets()
        return cleaned.length > 0 && cleaned.every(b => Number(b.min) <= Number(b.max))
      }
      if (this.selectedRule.label === 'CompetitiveIndex') {
        return this.valid && !!this.selectedRule.competitor && this.isValueEntered
      }
      if (this.selectedRule.label === 'CompetitiveMatch') {
        return this.valid && !!this.selectedRule.competitor
      }
      if (this.selectedRule.label === 'None') return true
      return this.isValueEntered
    },
    // Makes the deepest-level-wins behaviour visible.
    rulesetPreview() {
      const { filterLvl, targets } = this.resolveFilterTargets()
      if (!targets.length) {
        return 'No filter selected — this rule applies to the entire current selection.'
      }
      const noun = targets.length === 1 ? 'ruleset' : 'rulesets'
      return `Creates ${targets.length} ${noun}, one per ${this.getRuleDisplay(filterLvl).toLowerCase()}: ${targets.join(', ')}.`
    },
    // Warns when shallower selections are dropped by a deeper one.
    filterOverrideNotice() {
      const ignored = []
      if (this.selectedSubcategories.length) {
        if (this.selectedCategories.length) ignored.push('category')
        if (this.selectedDepartments.length) ignored.push('department')
      } else if (this.selectedCategories.length && this.selectedDepartments.length) {
        ignored.push('department')
      }
      if (!ignored.length) return ''
      const { filterLvl } = this.resolveFilterTargets()
      const verb = ignored.length > 1 ? 'selections are' : 'selection is'
      return `Only the most specific level (${this.getRuleDisplay(filterLvl).toLowerCase()}) applies — your ${ignored.join(' and ')} ${verb} ignored.`
    },
    // Department options come from /possible_values already filtered by the rule's
    // perishable batch type (data-driven), so no client-side department list is needed.
    filteredDepartments() {
      return this.departmentOptions
    },
    // Categories narrow to the selected department(s); when none are selected, show all
    // (still perishable-filtered upstream by the API).
    filteredCategories() {
      return [...new Set(
        this.departmentCategoryPairs
          .filter(p => !this.selectedDepartments.length || this.selectedDepartments.includes(p.column))
          .map(p => p.downstream_column)
      )].filter(Boolean)
    },
    // Subcategories narrow to the selected category(ies); when none are selected, fall back to
    // the categories visible under the chosen department(s) so the cascade still flows
    // department -> category -> subcategory. Perishable-filtered upstream by the API.
    filteredSubcategories() {
      const cats = this.selectedCategories.length ? this.selectedCategories : this.filteredCategories
      const catSet = new Set(cats)
      return [...new Set(
        this.categorySubcategoryPairs
          .filter(p => !catSet.size || catSet.has(p.column))
          .map(p => p.downstream_column)
      )].filter(Boolean)
    },
    noDataText() {
      if (this.isTprProRetailUser) {
        if (this.userStores.length === 0) {
          return "No TPR Pro stores available"
        }
        if (!this.store || !this.store.store_party_id) {
          return "No store selected"
        }
        return "No data available"
      }

      if (!this.isTprProRetailUser && this.disableRuleCreation) {
        return "No Ad Group selected"
      }

      return "No data available"
    }
  },
  methods: {
    flattenStoreParties(storeParties) {
      const isPriceProStore = store => {
        const flag = store?.is_price_pro_store
        return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
      }

      const childStoreItems = (Array.isArray(storeParties) ? storeParties : [])
        .filter(p => p.party_type === 'TPR_STORE_GROUP')
        .flatMap(group => group.children || [])
        .filter(store => isPriceProStore(store))
        .map(store => ({
          ...store,
          display_name: store.store_name
        }))

      const topLevelStoreItems = (Array.isArray(storeParties) ? storeParties : [])
        .filter(store => store.party_type === 'STORE' && isPriceProStore(store))
        .map(store => ({
          ...store,
          display_name: store.store_name
        }))
      
      const storeItems = [...childStoreItems, ...topLevelStoreItems]
        .filter((store, index, stores) =>
          index === stores.findIndex(candidate => candidate.store_party_id === store.store_party_id)
        )
        .sort((a, b) => a.store_name.localeCompare(b.store_name))

      return storeItems
    },
    // Groups plus every individual store (incl. group children, so per-store rules stay editable).
    buildStorePartyItems(storeParties) {
      const isPriceProStore = store => {
        const flag = store?.is_price_pro_store
        return typeof flag === 'string' ? flag.toLowerCase() === 'true' : Boolean(flag)
      }
      const list = Array.isArray(storeParties) ? storeParties : []
      const groups = list
        .filter(p => p.party_type === 'TPR_STORE_GROUP')
        .map(group => ({
          ...group,
          display_name: group.store_name,
          children: (group.children || [])
            .filter(isPriceProStore)
            .map(child => ({ ...child, display_name: child.store_name }))
        }))
        .filter(group => group.children.length > 0)
        .sort((a, b) => a.store_name.localeCompare(b.store_name))
      const stores = this.flattenStoreParties(storeParties)

      const items = []
      if (groups.length) items.push({ header: 'Store Groups' }, ...groups)
      if (stores.length) items.push({ header: 'Stores' }, ...stores)
      return items
    },
    firstSelectableStore() {
      return this.userStores.find(item => item && item.store_party_id) || null
    },
    formatDescription(description) {
      return description.split(/(?=If\s)/g).map(item => item.trim())
    },
    formatRuleValue(value, type) {
      if (type === "Percentage") {
        return `${value}%`
      } else if (type === "Number") {
        return `$${value}`
      }
    },
    // Human-readable summary of a competitive rule's selections (competitor, index %,
    // margin floor, rounding). Works on both a selectedRule and a rules-table row.
    competitiveSummary(rule) {
      if (!isCompetitiveRuleName(rule.rule_name)) return ''
      const parts = []
      if (rule.competitor) parts.push(competitorKeyToLabel(rule.competitor))
      if (rule.rule_name !== 'competitive_index_top_seller_match' && rule.rule_value !== '' && rule.rule_value != null)
        parts.push(`${rule.rule_value}% index`)
      if (rule.margin_threshold !== '' && rule.margin_threshold != null)
        parts.push(`min margin ${rule.margin_threshold}%`)
      if (rule.apply_rounding) parts.push('rounded')
      return parts.join(' · ')
    },
    getBatchTypes(value) {
      if (value == null) {
        return ["Perishable", "Non Perishable"]
      } else if (value === true) {
        return ["Perishable"]
      } else return ["Non Perishable"]
    },
    toggleDefinitions() {
      this.isDefinitionsVisible = !this.isDefinitionsVisible
    }, 
    getRuleValueRules() {
      const rule = this.selectedRule
      let rules = []

      if (rule.label === 'None' || rule.label === 'rounding') {
        return []
      }

      rules.push(value => {
        if (!value || isNaN(value)) {
          return 'Value must be a valid number'
        }
        return true
      })

      rules.push(value => {
        if (typeof value === 'string' && value.includes('.')) {
          const decimalPlaces = value.split('.')[1].length
          if (decimalPlaces > 2) {
            return 'Only up to 2 decimal places allowed'
          }
        }
        return true
      })

      if (rule.label === 'Percentage' && rule.rule_name === 'pass_thru_pct') {
        rules.push(value => {
          const numericValue = Number(value)
          if (numericValue < 70 || numericValue > 100) {
            return 'Value must be between 70%-100%'
          }
          return true
        })
      } else if (rule.label === 'Percentage' && rule.rule_name !== 'pass_thru_pct') {
        rules.push(value => {
          const numericValue = Number(value)
          if (numericValue < 0 || numericValue > 100) {
            return 'Value must be between 0-100%'
          }
          return true
        })
      } else if (rule.label === 'Number') {
        rules.push(value => {
          const numericValue = Number(value)
          if (numericValue < 0) {
            return 'Value must be greater than 0'
          }
          return true
        })
      }

      return rules
    },
    saveState() {
      if (!this.isTprProRetailUser) {
        if (this.selectedAdGroup) {
          sessionStorage.setItem("tpr_pro_ad_group", JSON.stringify(this.selectedAdGroup))
        }
      } else {
        if (this.store) {
          sessionStorage.setItem("tpr_pro_selected_store", JSON.stringify(this.store))
        }
      }
    },

    onDepartmentChange() {
      // prune any selected categories/subcategories no longer valid under the chosen departments
      const validCategories = new Set(this.filteredCategories)
      this.selectedCategories = this.selectedCategories.filter(c => validCategories.has(c))
      const validSubcategories = new Set(this.filteredSubcategories)
      this.selectedSubcategories = this.selectedSubcategories.filter(s => validSubcategories.has(s))
      this.isFilterComplete = true
    },
    onCategoryChange() {
      // prune any selected subcategories no longer valid under the chosen categories
      const validSubcategories = new Set(this.filteredSubcategories)
      this.selectedSubcategories = this.selectedSubcategories.filter(s => validSubcategories.has(s))
      this.isFilterComplete = true
    },
    onSubcategoryChange() {
      this.isFilterComplete = true
    },
    onLevelInput(level, value) {
      if (level === 'department') {
        this.selectedDepartments = value
        this.onDepartmentChange()
      } else if (level === 'category') {
        this.selectedCategories = value
        this.onCategoryChange()
      } else {
        this.selectedSubcategories = value
        this.onSubcategoryChange()
      }
    },
    // The rule filters at the deepest level that has selections; each selected value
    // becomes its own ruleset (one ruleset per department/category/subcategory).
    partyTargetsForSave() {
      if (!this.isTprProRetailUser) {
        return [{ party_id: this.selectedAdGroup.id, name: this.selectedAdGroup.name }]
      }
      if (this.storeGroupIsSelected) {
        return this.groupChildStores.map(child => ({ party_id: child.store_party_id, name: child.store_name }))
      }
      return [{ party_id: this.store.store_party_id, name: this.store.store_name }]
    },
    resolveFilterTargets() {
      if (this.selectedSubcategories.length) {
        return { filterLvl: 'subcategory', targets: this.selectedSubcategories }
      }
      if (this.selectedCategories.length) {
        return { filterLvl: 'category', targets: this.selectedCategories }
      }
      if (this.selectedDepartments.length) {
        return { filterLvl: 'department', targets: this.selectedDepartments }
      }
      return { filterLvl: '', targets: [] }
    },
    skipBatchTypeStep() {
      this.isBatchTypeComplete = true
      this.step = 4
    },
    skipFilterStep() {
      this.isFilterComplete = true
      this.step = 5
    },
    getRuleDisplay(key) {
      const ruleMapping = {
        'ad_group' : 'Ad Group',
        'store' : 'Store',
        'department' : 'Department',
        'category' : 'Category',
        'subcategory' : 'Subcategory'
      }
      return ruleMapping[key] || key
    },
    formatDate(date) {
      const options = {month: '2-digit', day: '2-digit', year: 'numeric'}
      return new Date(date).toLocaleDateString('en-US', options)
    },
    openRulesModal() {
      this.isRulesModalOpen = true
      this.editingRuleId = null
      this.editingGroupTargets = null
      this.selectedRule = { rule_display_name: "", rule_value: "", rule_name: "", rule_priority: "", perishable: null, enabled: true }
      this.step = 1
      this.isFilterComplete = false
      this.isRulesComplete = false
      this.resetCascadeSelections()
    },
    closeRulesModal() {
      this.isRulesModalOpen = false
      this.editingGroupTargets = null
      this.selectedRule = {}
      this.description = ''
      this.step = 1
      this.isFilterComplete = false
      this.isRulesComplete = false
      this.resetCascadeSelections()
      this.isRuleSelected = false
      this.isValueEntered = false
      this.isBatchTypeComplete = false
    },

    setInitialValue() {
      const ruleDisplayName = this.selectedRule.rule_display_name
      const ruleOption = this.ruleOptions.find(option => option.display_name === ruleDisplayName)

      if (ruleOption) {
        this.selectedRule.rule_name = ruleOption.name
        this.selectedRule.label = ruleOption.label
        if(ruleOption.name === 'pass_thru_pct'){
          this.selectedRule.rule_value = 80
          this.isValueEntered = true
          this.isRuleSelected = true
          this.step = 2
        } 
         else if(ruleOption.name === 'minimum_discount'){
          this.selectedRule.rule_value = 0.10
          this.isValueEntered = true
          this.isRuleSelected = true
          this.step = 2
        }
         else if (ruleOption.name === 'price_bracket') {
          this.$set(this.selectedRule, 'brackets',
            (this.selectedRule.brackets && this.selectedRule.brackets.length)
              ? this.selectedRule.brackets
              : [this.emptyBracket()])
          this.isRuleSelected = true
          this.isValueEntered = true
          this.step = 2
        }
         else if (isCompetitiveRuleName(ruleOption.name)) {
          // Match has no index to enter, so it is value-complete up front; the other two require an index.
          const isMatch = ruleOption.name === 'competitive_index_top_seller_match'
          this.selectedRule.rule_value = ''
          this.$set(this.selectedRule, 'competitor', '')
          this.$set(this.selectedRule, 'margin_threshold', '')
          this.$set(this.selectedRule, 'apply_rounding', false)
          this.isRuleSelected = true
          this.isValueEntered = isMatch
          this.step = 2
          this.getCompetitorsForCurrentScope()
        }
         else if (ruleOption.label === 'None') {
          this.selectedRule.rule_value = ''
          this.isRuleSelected = true
          this.step = 3
        } else {
          this.selectedRule.rule_value = 0
          this.isValueEntered = true
          this.isRuleSelected = true
          this.step = 2
        }
      }
    },

    // DMA-scoped competitor picker: single store -> its party_id; group -> union of children w/ divergence flag.
    async getCompetitorsForCurrentScope() {
      const ids = this.storeGroupIsSelected
        ? this.groupChildStores.map(c => c.store_party_id)
        : [this.store?.store_party_id].filter(Boolean)
      if (!ids.length) {
        this.competitorItems = []
        this.competitorDivergence = false
        return
      }
      this.isLoadingCompetitors = true
      try {
        const results = await Promise.all(ids.map(id => Rules.getCompetitors(id)))
        const lists = results.map(r => r.data || [])
        const union = [...new Set(lists.flat())].sort()
        this.competitorItems = union.map(key => ({ key, label: competitorKeyToLabel(key) }))
        this.competitorDivergence = this.storeGroupIsSelected && lists.some(l => l.length !== union.length)
      } catch (e) {
        console.error('getCompetitors error', e)
        this.competitorItems = []
        this.competitorDivergence = false
      } finally {
        this.isLoadingCompetitors = false
      }
    },
    handleValueInput() {
      const rules = this.getRuleValueRules()
      const value = this.selectedRule.rule_value
      const isValid = rules.every(rule => rule(value) === true)

      if (isValid && value) {
        this.isValueEntered = true
        this.step = 3
      }
    },
    // Bound directly in template so getRuleValueRules stays untouched for proven rules.
    indexPctRules,
    marginThresholdRules,
    handleCompetitiveInput() {
      // Mark the value step valid without advancing, so the optional margin
      // threshold + rounding fields stay reachable. Advance via the Next button.
      const competitorSet = !!this.selectedRule.competitor
      if (this.selectedRule.rule_name === 'competitive_index_top_seller_match') {
        this.isValueEntered = competitorSet  // Match has no index
        return
      }
      const indexValid = indexPctRules().every(r => r(this.selectedRule.rule_value) === true)
      this.isValueEntered = competitorSet && indexValid
    },
    advanceCompetitiveStep() {
      this.handleCompetitiveInput()
      if (this.isValueEntered) {
        this.step = 3
      }
    },
    handleBatchTypeInput() {
      this.resetCascadeSelections()
      this.isBatchTypeComplete = true
      // re-fetch department/category options filtered by the chosen batch type
      this.getPossibleValues()
      this.step = 4
    },
    completeRuleset() {
      if (!this.valid) {
        return
      }
      this.createOrUpdateRulesets()
    },
    emptyBracket() {
      return { min: null, max: null, units: null, price: null }
    },
    addBracket() {
      if (!Array.isArray(this.selectedRule.brackets)) {
        this.$set(this.selectedRule, 'brackets', [])
      }
      this.selectedRule.brackets.push(this.emptyBracket())
    },
    removeBracket(index) {
      this.selectedRule.brackets.splice(index, 1)
    },
    validBrackets() {
      const brackets = this.selectedRule.brackets || []
      return brackets.filter(bracket =>
        bracket.min !== null && bracket.min !== '' &&
        bracket.max !== null && bracket.max !== '' &&
        Number(bracket.units) >= 1 && bracket.price !== null && bracket.price !== ''
      )
    },
    handleBracketInput() {
      const cleaned = this.validBrackets()
      if (cleaned.length === 0) {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Add at least one complete bracket (min, max, units, price).'
        })
        return
      }
      const invalidRange = cleaned.some(bracket => Number(bracket.min) > Number(bracket.max))
      if (invalidRange) {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Each bracket min must be less than or equal to its max.'
        })
        return
      }
      this.isValueEntered = true
      this.step = 3
    },
    formatBrackets(brackets) {
      if (!brackets || !brackets.length) return ''
      return brackets
        .map(bracket => `$${bracket.min}-$${bracket.max} → ${bracket.units}/$${bracket.price}`)
        .join(', ')
    },
    async toggleEnabled(rule) {
      try {
        this.isLoading = true
        const isBracket = rule.label === 'Bracket'
        const isMatch = rule.rule_name === 'competitive_index_top_seller_match'
        const ruleEntry = {
          display_name: rule.rule_display_name,
          name: rule.rule_name,
          value: isBracket || isMatch ? null : Number(rule.rule_value)
        }
        if (isBracket) {
          ruleEntry.brackets = rule.brackets || []
        }
        if (isCompetitiveRuleName(rule.rule_name)) {
          Object.assign(ruleEntry, buildCompetitiveRuleEntry(rule))
        }
        const toggleTargets = (this.storeGroupIsSelected && rule.group_targets?.length)
          ? rule.group_targets
          : [{ ruleset_id: rule.ruleset_id, party_id: rule.party_id, name: rule.name }]
        await Promise.all(toggleTargets.map(target => Rules.updateRulesets({
          ruleset_id: target.ruleset_id,
          party_id: target.party_id,
          name: target.name,
          level: rule.level,
          filter_lvl: rule.filter_lvl || '',
          filter_value: rule.filter_value || '',
          perishable: rule.perishable,
          rule_list: [ruleEntry],
          enabled: rule.enabled === false,
          modified_by: this.$auth.user.email
        })))
        await this.getRulesets()
      } catch (err) {
        console.error('Error toggling ruleset enabled state:', err)
      } finally {
        this.isLoading = false
      }
    },
    async editRule(rule) {
      this.isRulesModalOpen = true
      this.editingRuleId = rule.ruleset_id
      this.editingGroupTargets = rule.group_targets || null
      this.step = 1

      this.isRuleSelected = true
      this.isValueEntered = true
      this.isFilterComplete = true
      this.isRulesComplete = true
      this.isBatchTypeComplete = true

      this.selectedRule = {
        rule_display_name: rule.rule_display_name,
        rule_value: rule.label === 'None' ? '' : rule.rule_value,
        rule_priority: rule.rule_priority,
        rule_name: rule.rule_name,
        label: rule.label || 'None',
        created_date: rule.created_date,
        perishable: rule.perishable,
        brackets: rule.brackets ? rule.brackets.map(bracket => ({ ...bracket })) : undefined,
        competitor: rule.competitor || '',
        margin_threshold: rule.margin_threshold ?? '',
        apply_rounding: rule.apply_rounding ?? false,
        enabled: rule.enabled !== false
      }
      // competitive edit needs the DMA competitor list so the picker isn't disabled/empty
      if (isCompetitiveRuleName(rule.rule_name)) {
        await this.getCompetitorsForCurrentScope()
      }
      // load options for this rule's batch type, then prefill the cascade selections
      await this.getPossibleValues()
      this.prefillCascade(rule.filter_lvl || '', rule.filter_value || '')
    },
    resetCascadeSelections() {
      this.selectedDepartments = []
      this.selectedCategories = []
      this.selectedSubcategories = []
    },
    prefillCascade(filterLvl, filterValue) {
      this.resetCascadeSelections()
      if (!filterValue) return
      if (filterLvl === 'department') {
        this.selectedDepartments = [filterValue]
      } else if (filterLvl === 'category') {
        this.selectedCategories = [filterValue]
        const dept = this.departmentCategoryPairs.find(p => p.downstream_column === filterValue)?.column
        this.selectedDepartments = dept ? [dept] : []
      } else if (filterLvl === 'subcategory') {
        this.selectedSubcategories = [filterValue]
        const category = this.categorySubcategoryPairs.find(p => p.downstream_column === filterValue)?.column
        this.selectedCategories = category ? [category] : []
        const dept = category
          ? this.departmentCategoryPairs.find(p => p.downstream_column === category)?.column
          : null
        this.selectedDepartments = dept ? [dept] : []
      }
    },
    async onSelectionChange() {
      this.isLoading = true
      try {
        this.rulesData = []
        this.saveState()
        await Promise.all([
          this.getRules(this.isTprProRetailUser),
          this.getRulesets()
        ])
      } catch (err) {
        console.error('Error during selection change:', err)
      } finally {
        this.isLoading = false
      }
    }, 
    async createOrUpdateRulesets() {
      if (!this.valid) {
        return
      }
      const isBracket = this.selectedRule?.rule_name === 'price_bracket'
      // Match carries no index, so its value must persist as null (Number('') would coerce to 0).
      const isMatch = this.selectedRule?.rule_name === 'competitive_index_top_seller_match'

      const ruleEntry = {
        display_name: this.selectedRule?.rule_display_name,
        name: this.selectedRule?.rule_name,
        value: isBracket || isMatch ? null : Number(this.selectedRule?.rule_value)
      }
      if (isBracket) {
        ruleEntry.brackets = this.validBrackets().map(bracket => ({
          min: Number(bracket.min),
          max: Number(bracket.max),
          units: Number(bracket.units),
          price: Number(bracket.price)
        }))
        if (ruleEntry.brackets.length === 0) {
          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: 'Add at least one complete bracket before saving.'
          })
          return
        }
      }

      // Competitive add-on fields ride inside ruleEntry; non-competitive payloads stay byte-identical.
      if (isCompetitiveRuleName(this.selectedRule?.rule_name)) {
        Object.assign(ruleEntry, buildCompetitiveRuleEntry(this.selectedRule))
      }

      const base = {
        level: this.isTprProRetailUser ? 'store' : 'ad_group',
        modified_by: this.$auth.user.email,
        created_date: this.selectedRule?.created_date,
        rule_list: [ruleEntry],
        perishable: this.selectedRule?.perishable,
        enabled: this.selectedRule?.enabled !== false
      }

      const partyTargets = this.partyTargetsForSave()

      // Filter at the deepest level with selections; create one ruleset per selected
      // value (or a single unfiltered ruleset when nothing is selected).
      const { filterLvl, targets } = this.resolveFilterTargets()
      const filterTargets = targets.length ? targets : ['']

      // Editing maps to a single ruleset; refuse to silently drop extra selections.
      if (this.editingRuleId && targets.length > 1) {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Editing a rule supports a single filter value. Remove the extra selections, or create a new rule to apply this across multiple values.'
        })
        return
      }

      try {
        this.isSavingRule = true
        this.isLoading = true
        if (this.editingRuleId) {
          const editTargets = (this.storeGroupIsSelected && this.editingGroupTargets?.length)
            ? this.editingGroupTargets
            : [{ ...partyTargets[0], ruleset_id: this.editingRuleId }]
          await Promise.all(editTargets.map(target => this.updateRulesets({
            ...base,
            ruleset_id: target.ruleset_id,
            party_id: target.party_id,
            name: target.name,
            filter_lvl: targets.length ? filterLvl : '',
            filter_value: targets[0] || ''
          })))
        } else {
          const creations = []
          partyTargets.forEach(party => filterTargets.forEach(target => creations.push(this.createRulesets({
            ...base,
            party_id: party.party_id,
            name: party.name,
            created_by: this.$auth.user.email,
            filter_lvl: target ? filterLvl : '',
            filter_value: target || ''
          }))))
          await Promise.all(creations)
        }
        this.closeRulesModal()
        await this.getRulesets()
      } catch (err) {
        this.handleRulesetError(err)
      } finally {
        this.isSavingRule = false
        this.isLoading = false
      }
    },
    handleRulesetError(err) {
      const status = err.response?.status
      const message = err.response?.data?.message
      if (status === 406) {
        const minValue = err.response.data?.extras?.value?.min_value
        const snackbarText = minValue === 100 ? message : `${message} Minimum value allowed: ${minValue}`
        this.$store.dispatch('setSnackbar', { status: 'error', text: snackbarText })
      } else if (message) {
        this.$store.dispatch('setSnackbar', { status: 'error', text: message })
      } else {
        console.error('Ruleset operation failed:', err)
        this.$store.dispatch('setSnackbar', { status: 'error', text: 'Unable to save ruleset. Please try again.' })
      }
    },
    async initializeData() {
      try {
        this.isLoading = true
        if (!this.isTprProRetailUser) {
          const savedAdGroup = sessionStorage.getItem("tpr_pro_ad_group")
          this.selectedAdGroup = savedAdGroup ? JSON.parse(savedAdGroup) : this.userAdGroups[0]
        } else {
          const stores = await this.getStores()
          const savedStore = sessionStorage.getItem("tpr_pro_selected_store")
          const savedStoreId = savedStore ? JSON.parse(savedStore)?.store_party_id : null
          this.store = stores.find(store => store.store_party_id === savedStoreId) || this.firstSelectableStore()

          if (!this.store) {
            this.alert('You are not associated with any TPR Pro stores. Please contact support for access.')
            this.isLoading = false
            this.$router.replace('/')
            return
          }
        }

        if (this.selectedAdGroup || this.store) {
          this.getRulesets()
          this.getRules(this.isTprProRetailUser)
          this.getPossibleValues()
        }
        this.saveState()

      } catch (err) {
        console.error("Error initializing data:", err)
      } finally {
        this.isLoading = false
      }
    },
    async getPossibleValues() {
      try {
        this.isLoadingFilters = true
        const perishable = this.selectedRule?.perishable ?? null
        const [deptCats, catSubcats] = await Promise.all([
          Rules.getPossibleValues('department', 'category', perishable),
          Rules.getPossibleValues('category', 'subcategory', perishable)
        ])
        this.departmentCategoryPairs = deptCats.data
        this.departmentOptions = [...new Set(deptCats.data.map(p => p.column))]
        this.categorySubcategoryPairs = catSubcats.data
      } catch (err) {
        console.error('Get possible values error: ', err)
      } finally {
        this.isLoadingFilters = false
      }
    },
    async getRules() {
      try {
        const res = await Rules.getRules(this.isTprProRetailUser)
        this.ruleOptions = res.data.sort((a, b) => a.priority - b.priority)
      } catch (err) {
        console.error("Error fetching rules:", err)
      }
    },
    async getStores(){
      try{
        const storeParties = await this.$store.dispatch('getUserProfileTprStoreGroups')
        this.userStores = this.useStoreParties
          ? this.buildStorePartyItems(storeParties)
          : this.flattenStoreParties(storeParties)
        return this.userStores
      } catch (err) {
        console.log('Get stores Error: ', err)
        this.userStores = []
        return []
      }
    },
    async getRulesets() {
      try {
        this.ruleDrift = false
        this.ruleDeviations = []

        if (this.isTprProRetailUser) {
          if (this.userStores.length === 0) {
            this.alert("No Stores found for user, cannot retrieve rule sets.")
            return
          }
          if (!this.store || !this.store.store_party_id) {
            this.alert('Select a TPR Pro store to view rule sets.')
            return
          }
          if (this.storeGroupIsSelected) {
            const children = this.groupChildStores
            if (children.length === 0) { this.rulesData = []; return }
            const results = await Promise.all(
              children.map(child => Rules.getRulesets(child.store_party_id))
            )
            this.rulesData = this.intersectStoreRulesets(results.map(res => res.data || []), children)
            return
          }
          const res = await Rules.getRulesets(this.store.store_party_id)
          this.rulesData = this.decorateRulesets(res.data)
          return
        }

        if (this.selectedAdGroup == null || this.selectedAdGroup.id == null) {
          this.alert("No Ad Groups found for user, cannot retrieve rule sets.")
          return
        }
        const res = await Rules.getRulesets(this.selectedAdGroup.id)
        this.rulesData = this.decorateRulesets(res.data)
      } catch (err) {
        console.error("Error fetching rulesets:", err)
      }
    },
    decorateRulesets(rulesets) {
      return (rulesets || []).map(ruleset => ({
        ...ruleset,
        formated_created_date: this.formatDate(ruleset.created_date),
        formated_modified_date: this.formatDate(ruleset.modified_date),
      }))
    },
    // Store-independent identity; child rulesets matching this are "the same rule".
    rulesetSignature(ruleset) {
      const brackets = Array.isArray(ruleset.brackets)
        ? ruleset.brackets.map(b => `${b.min}|${b.max}|${b.units}|${b.price}`).sort()
        : []
      // competitiveSignatureFields owns value + competitor/margin/rounding; non-competitive get null/false defaults.
      return JSON.stringify({
        name: ruleset.rule_name,
        ...competitiveSignatureFields(ruleset),
        brackets,
        filter_lvl: ruleset.filter_lvl || '',
        filter_value: ruleset.filter_value || '',
        perishable: ruleset.perishable ?? null,
        enabled: ruleset.enabled !== false
      })
    },
    // Keep rules shared by every store (with group_targets for fan-out); partial rules become deviations.
    intersectStoreRulesets(perStoreRulesets, children) {
      const bySignature = new Map()
      perStoreRulesets.forEach((rulesets, index) => {
        const child = children[index]
        const seen = new Set()
        ;(rulesets || []).forEach(ruleset => {
          const signature = this.rulesetSignature(ruleset)
          if (seen.has(signature)) return
          seen.add(signature)
          if (!bySignature.has(signature)) bySignature.set(signature, { rep: ruleset, targets: [] })
          bySignature.get(signature).targets.push({
            ruleset_id: ruleset.ruleset_id,
            party_id: child.store_party_id,
            name: child.store_name
          })
        })
      })

      const common = []
      const deviations = []
      bySignature.forEach(entry => {
        if (entry.targets.length === children.length) {
          common.push({ ...entry.rep, group_targets: entry.targets })
        } else {
          const presentIds = new Set(entry.targets.map(target => target.party_id))
          const missingChildren = children.filter(child => !presentIds.has(child.store_party_id))
          deviations.push({
            rep: entry.rep,
            present: entry.targets.map(target => target.name),
            missing: missingChildren.map(child => child.store_name),
            presentTargets: entry.targets,
            missingTargets: missingChildren.map(child => ({ party_id: child.store_party_id, name: child.store_name }))
          })
        }
      })
      this.ruleDeviations = deviations
      this.ruleDrift = deviations.length > 0
      return this.decorateRulesets(common)
    },
    async createRulesets(ruleData) {
      // POST only; createOrUpdateRulesets handles modal close, refresh, and error reporting
      await Rules.createRulesets(ruleData)
    },
    async updateRulesets(ruleData) {
      // PUT only; createOrUpdateRulesets handles modal close, refresh, and error reporting
      await Rules.updateRulesets(ruleData)
    },
    async applyDeviationToAll(deviation) {
      const rep = deviation.rep
      const isBracket = rep.label === 'Bracket'
      const isMatch = rep.rule_name === 'competitive_index_top_seller_match'
      const ruleEntry = {
        display_name: rep.rule_display_name,
        name: rep.rule_name,
        value: isBracket || isMatch ? null : Number(rep.rule_value)
      }
      if (isBracket) ruleEntry.brackets = rep.brackets || []
      if (isCompetitiveRuleName(rep.rule_name)) {
        Object.assign(ruleEntry, buildCompetitiveRuleEntry(rep))
      }
      try {
        this.isLoading = true
        await Promise.all(deviation.missingTargets.map(target => this.createRulesets({
          level: rep.level || 'store',
          party_id: target.party_id,
          name: target.name,
          created_by: this.$auth.user.email,
          modified_by: this.$auth.user.email,
          filter_lvl: rep.filter_lvl || '',
          filter_value: rep.filter_value || '',
          perishable: rep.perishable,
          rule_list: [ruleEntry],
          enabled: rep.enabled !== false
        })))
        await this.getRulesets()
      } catch (err) {
        this.handleRulesetError(err)
      } finally {
        this.isLoading = false
      }
    },
    async removeDeviationFromAll(deviation) {
      try {
        this.isLoading = true
        await Promise.all(deviation.presentTargets.map(target => Rules.deleteRulesets(target.ruleset_id)))
        await this.getRulesets()
      } catch (err) {
        console.error("Error removing deviation:", err)
      } finally {
        this.isLoading = false
      }
    },
    async deleteRule(rule) {
      try {
        const rulesetIds = (this.storeGroupIsSelected && rule.group_targets?.length)
          ? rule.group_targets.map(target => target.ruleset_id)
          : [rule.ruleset_id]
        await Promise.all(rulesetIds.map(id => Rules.deleteRulesets(id)))
        await this.getRulesets()
      } catch (err) {
        console.error("Error deleting rule:", err)
      }
    },
    openShuttleModal(rule) {
      this.shuttleRuleset = rule
      this.isShuttleModalOpen = true
    }
  }
}
</script>
<style scoped>
.displayedValues {
  font-weight: bolder;
  font-style: italic;
  padding-left: 10px;
}
.definitions-title {
  text-align: center;
}

.definition-list {
  display: none;
  margin-left: 20px;
}
.definition-term {
  padding: 5px;
}

.definition-term:hover .definition-list {
  display: block;
}
.theme--light.v-data-table
  .v-data-table-header
  th.sortable
  .v-data-table-header__icon {
  color: #fff !important
}
.v-card-text {
  padding: 8px 16px
}
.v-card-actions {
  padding: 8px 16px
}
.ruleNames{
  background-color: rgb(225, 222, 222) !important
}
</style>
